import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Download, Loader2, FileText, CheckCircle, XCircle, MinusCircle } from 'lucide-react';
import { useChecklist } from '@/contexts/ChecklistContext';
import { useAuth } from '@/contexts/AuthContext';
import { useDocumentConfig } from '@/hooks/useDocumentConfig';
import { CHECKLIST_BLOCKS } from '@/lib/checklistBlocks';
import { getChecklistTemplate } from '@/lib/checklists/registry';
import { PdfDocumentBuilder, type EmpresaInfo, type DocumentConfig } from '@/lib/pdfDocumentEngine';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ChecklistDetalleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklistId: string;
  fecha: string;
  tipoRevision?: string;
  tecnicoNombre: string;
  clienteNombre: string;
  observacionesGenerales?: string | null;
  firmaTecnico?: string | null;
  docConfig?: Partial<DocumentConfig>;
  sistemaTipo?: string;
}

interface RespuestaGuardada {
  bloque_id: string;
  pregunta_id: string;
  pregunta_texto: string;
  respuesta: string | null;
  observaciones: string | null;
}

const RESPUESTA_ICON: Record<string, React.ReactNode> = {
  si: <CheckCircle className="h-4 w-4 text-green-500" />,
  no: <XCircle className="h-4 w-4 text-red-500" />,
  na: <MinusCircle className="h-4 w-4 text-muted-foreground" />,
};

const RESPUESTA_LABEL: Record<string, string> = {
  si: 'SÍ',
  no: 'NO',
  na: 'N/A',
};

export const ChecklistDetalleDialog: React.FC<ChecklistDetalleDialogProps> = ({
  open,
  onOpenChange,
  checklistId,
  fecha,
  tipoRevision,
  tecnicoNombre,
  clienteNombre,
  observacionesGenerales,
  firmaTecnico,
  docConfig: docConfigProp,
  sistemaTipo,
}) => {
  const { obtenerRespuestasChecklist } = useChecklist();
  const { empresa } = useAuth();
  const { docConfig } = useDocumentConfig(docConfigProp);
  const [respuestas, setRespuestas] = useState<RespuestaGuardada[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    if (open && checklistId) {
      const load = async () => {
        setLoading(true);
        const { data } = await obtenerRespuestasChecklist(checklistId);
        if (data) setRespuestas(data as RespuestaGuardada[]);
        setLoading(false);
      };
      load();
    }
  }, [open, checklistId]);

  const getRespuestasPorBloque = (bloqueId: string) =>
    respuestas.filter(r => r.bloque_id === bloqueId);

  // Build groups dynamically so we render every bloque actually saved,
  // not just the legacy hardcoded ones.
  const grupos = React.useMemo(() => {
    const template = sistemaTipo ? getChecklistTemplate(sistemaTipo) : null;
    const legacyTitle: Record<string, string> = Object.fromEntries(
      CHECKLIST_BLOCKS.map(b => [b.id, b.title])
    );
    const templateTitle: Record<string, string> = template
      ? Object.fromEntries(template.protocolos.map(p => [p.id, p.titulo]))
      : {};
    const order: string[] = [];
    const seen = new Set<string>();
    // Preserve template order first
    if (template) {
      template.protocolos.forEach(p => {
        if (!seen.has(p.id)) { order.push(p.id); seen.add(p.id); }
      });
    }
    // Then any extra bloque_id that appears in responses
    respuestas.forEach(r => {
      if (!seen.has(r.bloque_id)) { order.push(r.bloque_id); seen.add(r.bloque_id); }
    });
    return order
      .map(id => ({
        id,
        title: templateTitle[id] || legacyTitle[id] || id,
        items: respuestas.filter(r => r.bloque_id === id),
      }))
      .filter(g => g.items.length > 0);
  }, [respuestas, sistemaTipo]);

  const respuestaTone = (r?: string | null): 'ok' | 'ko' | 'na' | 'neutral' => {
    if (r === 'si') return 'ok';
    if (r === 'no') return 'ko';
    if (r === 'na') return 'na';
    return 'neutral';
  };

  const generatePDF = async () => {
    setGeneratingPdf(true);
    try {
      const empresaInfo: EmpresaInfo | null = empresa ? {
        nombre: empresa.nombre,
        razon_social: empresa.razon_social,
        nif_cif: empresa.nif_cif,
        telefono: empresa.telefono,
        email: empresa.email,
        logo_url: empresa.logo_url,
        sello_url: (empresa as any).sello_url,
        numero_dgp: (empresa as any).numero_dgp,
        numero_reci: (empresa as any).numero_reci,
        direccion_calle: (empresa as any).direccion_calle,
        direccion_numero: (empresa as any).direccion_numero,
        codigo_postal: (empresa as any).codigo_postal,
        ciudad: (empresa as any).ciudad,
        provincia: (empresa as any).provincia,
      } : null;


      const builder = await PdfDocumentBuilder.create(empresaInfo, docConfig);

      builder.addBrandedHeader({
        titulo: 'Checklist de Seguridad',
        estado: tipoRevision || undefined,
        fechaEmision: `Emitido: ${new Date().toLocaleDateString('es-ES')}`,
      });

      const fechaFormatted = format(new Date(fecha), "dd 'de' MMMM yyyy", { locale: es });
      builder.addInfoCardRow(
        {
          title: 'Cliente',
          fields: [{ label: 'Razón social', value: clienteNombre }],
        },
        {
          title: 'Datos de la revisión',
          fields: [
            { label: 'Técnico', value: tecnicoNombre },
            { label: 'Fecha', value: fechaFormatted },
            ...(tipoRevision ? [{ label: 'Tipo', value: tipoRevision }] : []),
          ],
        },
      );

      if (observacionesGenerales) {
        builder.addSection('Observaciones Generales');
        builder.addQuoteBlock(observacionesGenerales);
      }

      builder.addSection('Checklist de Mantenimiento');

      grupos.forEach(block => {
        if (block.items.length === 0) return;
        builder.addBlockSubtitle(block.title);
        block.items.forEach(resp => {
          const label = resp.respuesta ? (RESPUESTA_LABEL[resp.respuesta] || resp.respuesta) : '—';
          builder.addChecklistItem({
            code: resp.pregunta_id,
            text: resp.pregunta_texto,
            badge: label,
            badgeTone: respuestaTone(resp.respuesta),
            observaciones: resp.observaciones || undefined,
          });
        });
        builder.addSpacer(3);
      });

      builder.addSignatureBlock([
        { label: 'Firma del técnico', imageData: firmaTecnico, nombre: tecnicoNombre },
        { label: 'Sello de la empresa', esEmpresa: true },
      ]);

      const fileName = `checklist_seguridad_${format(new Date(fecha), 'yyyyMMdd')}.pdf`;
      builder.save(fileName);
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] h-[90vh] sm:h-auto sm:max-h-[85vh] flex flex-col p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Detalle del Checklist
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto pr-1 sm:pr-4 -mr-1 sm:-mr-4">
            <div className="space-y-4 pb-4">
              {/* Info */}
              <Card>
                <CardContent className="pt-4 text-sm space-y-1">
                  <p><span className="font-medium">Cliente:</span> {clienteNombre}</p>
                  <p><span className="font-medium">Técnico:</span> {tecnicoNombre}</p>
                  <p><span className="font-medium">Fecha:</span> {format(new Date(fecha), 'dd/MM/yyyy', { locale: es })}</p>
                  {tipoRevision && (
                    <p><span className="font-medium">Tipo:</span> <Badge variant="secondary" className="text-xs capitalize ml-1">{tipoRevision}</Badge></p>
                  )}
                  {observacionesGenerales && (
                    <p><span className="font-medium">Observaciones:</span> {observacionesGenerales}</p>
                  )}
                </CardContent>
              </Card>

              {/* Respuestas por bloque */}
              {grupos.map(block => {
                const blockRespuestas = block.items;
                return (
                  <Card key={block.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
                        {block.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {blockRespuestas.map(resp => (
                        <div key={resp.pregunta_id} className="space-y-0.5">
                          <div className="flex items-center justify-between py-1 text-sm">
                            <div className="flex items-center gap-2 flex-1">
                              {RESPUESTA_ICON[resp.respuesta || ''] || <MinusCircle className="h-4 w-4 text-muted-foreground" />}
                              <span className="text-xs font-mono text-muted-foreground">{resp.pregunta_id}</span>
                              <span className="flex-1">{resp.pregunta_texto}</span>
                            </div>
                            <Badge variant="outline" className="text-xs ml-2">
                              {resp.respuesta ? (RESPUESTA_LABEL[resp.respuesta] || resp.respuesta) : '---'}
                            </Badge>
                          </div>
                          {resp.observaciones && (
                            <p className="text-xs text-muted-foreground ml-8 pb-1">
                              Obs: {resp.observaciones}
                            </p>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}

              {/* Firma */}
              {firmaTecnico && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Firma del técnico</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <img src={firmaTecnico} alt="Firma" className="max-h-24 border rounded" />
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
          <Button onClick={generatePDF} disabled={generatingPdf || loading} className="gap-2">
            {generatingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Descargar PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
